import { useState } from 'react';

import { Skeleton } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { getCDNOptimizedImage } from '@arrived/common';
import { ArrivedLogoIcon } from '@arrived/images';
import { Offering } from '@arrived/models';

export interface PropertyThumbnailProps {
  className?: string;
  dataE2e?: string;
  fundedPercentage?: number;
  isForcedHeight?: boolean;
  isScalableImage?: boolean;
  offering?: Offering;
  propertyName?: string;
  thumbnailHeight?: string;
  thumbnailRatio?: string;
  thumbnailSrc?: string;
  thumbnailWidth?: string;
  wrapperHeight?: string;
  wrapperWidth?: string;
}

export const StyledPropertyThumbnail = styled.div<{
  $imageHeight?: string;
  $imageWidth?: string;
  $isForcedHeight?: boolean;
  $wrapperHeight?: string;
  $wrapperWidth?: string;
}>`
  &.property-thumbnail-wrapper {
    flex: 1 1 auto;
    width: ${({ $wrapperWidth }) => $wrapperWidth || 'auto'};
    height: ${({ $wrapperHeight }) => $wrapperHeight || 'auto'};
    line-height: 0; // Fixes an issue where whitespace is rendered which leaves a small white section below the images

    .property-thumbnail-image-wrapper {
      position: relative;

      .property-thumbnail-status-flag {
        position: absolute;
      }

      .property-thumbnail-image,
      .property-thumbnail-image-placeholder {
        object-fit: cover;
      }

      .property-thumbnail-image {
        width: 100%;
        height: ${(props) => (props.$isForcedHeight ? props.$imageHeight : 'auto')};
        margin: 0;
      }

      .property-thumbnail-image-placeholder {
        z-index: 2;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }

    &.loading {
      .property-thumbnail-image-wrapper {
        min-width: ${({ $imageWidth }) => $imageWidth};
        min-height: ${({ $imageHeight }) => $imageHeight};

        .property-thumbnail-image {
          visibility: hidden;
        }
      }
    }
  }
`;

export const PropertyThumbnail = ({
  className,
  dataE2e,
  isForcedHeight,
  isScalableImage,
  propertyName,
  thumbnailHeight,
  thumbnailRatio,
  thumbnailSrc,
  thumbnailWidth,
  wrapperHeight,
  wrapperWidth,
}: PropertyThumbnailProps) => {
  const [isImageLoading, setIsImageLoading] = useState(true);

  const doubleSizedWidth = isScalableImage && thumbnailWidth ? `${parseInt(thumbnailWidth) * 2}px` : thumbnailWidth;

  return (
    <StyledPropertyThumbnail
      $imageHeight={thumbnailHeight}
      $imageWidth={thumbnailWidth}
      $isForcedHeight={isForcedHeight}
      $wrapperHeight={wrapperHeight}
      $wrapperWidth={wrapperWidth}
      className={clsx('property-thumbnail-wrapper', className, isImageLoading && 'loading')}
    >
      <div className="property-thumbnail-image-wrapper">
        {thumbnailSrc ? (
          <>
            <img
              alt={propertyName}
              onLoad={() => {
                setIsImageLoading(false);
              }}
              src={getCDNOptimizedImage({
                imgUrl: thumbnailSrc,
                ratio: thumbnailRatio,
                width: doubleSizedWidth,
              })}
              className="property-thumbnail-image"
              data-e2e={dataE2e}
            />
            {isImageLoading ? (
              <Skeleton variant="rectangular" className="property-thumbnail-image-placeholder" />
            ) : null}
          </>
        ) : (
          <ArrivedLogoIcon height={thumbnailHeight} width={thumbnailWidth} />
        )}
      </div>
    </StyledPropertyThumbnail>
  );
};
